<template>
  <b-modal
    id="exception-approval-modal"
    v-model="isOpen"
    :title="replaceMode
      ? 'Alterar arquivo da aprovação por exceção'
      : 'Solicitar aprovação por exceção'"
    :no-close-on-backdrop="loading"
    :no-close-on-esc="loading"
    centered
    hide-footer
    @hidden="resetForm"
  >
    <div class="exception-content">

      <p class="exception-description">
        {{ replaceMode
          ? 'Anexe o novo arquivo para substituir o documento atual.'
          : 'Anexe o documento necessário para solicitar a aprovação por exceção.' }}
      </p>

      <div
        class="upload-area"
        :class="{ 'upload-area-active': isDragging }"
        @dragover.prevent="isDragging = true"
        @dragleave.prevent="isDragging = false"
        @drop.prevent="handleDrop"
        @click="openFileSelector"
      >
        <input
          ref="fileInput"
          type="file"
          accept=".pdf,.eml,application/pdf,message/rfc822"
          class="d-none"
          @change="handleFileChange"
        >

        <feather-icon
          icon="UploadCloudIcon"
          size="32"
          class="upload-icon"
        />

        <p class="upload-title">
          Arraste o arquivo aqui
        </p>

        <span class="upload-subtitle">
          ou clique para selecionar
        </span>

        <span class="upload-format">
          Formatos permitidos: PDF ou EML
        </span>
      </div>

      <div
        v-if="selectedFile"
        class="selected-file"
      >
        <div>
          <feather-icon
            icon="FileIcon"
            size="20"
          />

          <span>{{ selectedFile.name }}</span>
        </div>

        <b-button
          variant="link"
          class="remove-file"
          @click.stop="removeFile"
        >
          <feather-icon
            icon="XIcon"
            size="18"
          />
        </b-button>
      </div>

      <div class="input-container">
        <label for="exception-description">
          Observação
        </label>

        <b-form-input
          id="exception-description"
          v-model="description"
          placeholder="Digite uma observação"
          autocomplete="off"
        />
      </div>

     <div class="modal-footer">
        <b-button
          variant="outline-secondary"
          :disabled="loading"
          @click="close"
        >
          Cancelar
        </b-button>

        <b-button
          variant="primary"
          :disabled="!selectedFile || loading"
          type="button"
          class="d-flex align-items-center justify-content-center"
          @click.stop="approve"
        >
          <div v-if="loading" class="dotted-spinner"></div>

          <span v-else>
            {{ replaceMode ? 'Substituir arquivo' : 'Aprovar' }}
          </span>
        </b-button>
      </div>

    </div>
  </b-modal>
</template>

<script>
import {
  BModal,
  BButton,
  BFormInput
} from 'bootstrap-vue'

export default {
  name: 'ExceptionApprovalModal',

  components: {
    BModal,
    BButton,
    BFormInput
  },

  props: {
    value: {
      type: Boolean,
      default: false
    },

    replaceMode: {
      type: Boolean,
      default: false
    }, 

     loading: {
      type: Boolean,
      default: false
    }
  },

  data() {
    return {
      selectedFile: null,
      description: '',
      isDragging: false
    }
  },

  computed: {
    isOpen: {
      get() {
        return this.value
      },

      set(value) {
        this.$emit('input', value)
      }
    }
  },

  methods: {
    openFileSelector() {
      this.$refs.fileInput.click()
    },

    handleFileChange(event) {
      const [file] = event.target.files

      if (file && this.isAllowedFile(file)) {
        this.selectedFile = file
      } else if (file) {
        this.$toast.error('Selecione um arquivo PDF ou EML.')
        event.target.value = ''
      }
    },

    handleDrop(event) {
      this.isDragging = false

      const [file] = event.dataTransfer.files

      if (file && this.isAllowedFile(file)) {
        this.selectedFile = file
      } else if (file) {
        this.$toast.error('Selecione um arquivo PDF ou EML.')
      }
    },

    isAllowedFile(file) {
      const fileName = file.name.toLowerCase()

      return fileName.endsWith('.pdf') || fileName.endsWith('.eml')
    },

    removeFile() {
      this.selectedFile = null

      if (this.$refs.fileInput) {
        this.$refs.fileInput.value = ''
      }
    },

    close() {
      this.isOpen = false
    },

    approve() {
      if (!this.selectedFile) {
        return
      }

      this.$emit('approve', {
        file: this.selectedFile,
        description: this.description
      })
    },

    resetForm() {
      this.selectedFile = null
      this.description = ''
      this.isDragging = false

      if (this.$refs.fileInput) {
        this.$refs.fileInput.value = ''
      }
    }
  }
}
</script>

<style scoped>


.upload-area {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 180px;
  padding: 25px;
  border: 2px dashed #d8d6de;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.2s ease;
}

.upload-area:hover,
.upload-area-active {
  border-color: #4267a9;
}

.upload-icon {
  margin-bottom: 10px;
  color: #4267a9;
}

.upload-title {
  margin: 0 0 4px;
  font-size: 14px;
  font-weight: 600;
}

.upload-subtitle {
  font-size: 12px;
}

.upload-format {
  margin-top: 10px;
  font-size: 11px;
}

.selected-file {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-top: 12px;
  padding: 10px 12px;
  border: 1px solid #ebe9f1;
  border-radius: 6px;
  font-size: 13px;
}

.selected-file > div {
  display: flex;
  align-items: center;
  gap: 8px;
  overflow: hidden;
}

.selected-file span {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.remove-file {
  padding: 0;
  color: #ea5455;
}

.input-container {
  margin-top: 20px;
}

.input-container label {
  display: block;
  margin-bottom: 6px;
  font-size: 13px;
  font-weight: 600;
}

.modal-footer {
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  margin-top: 25px;
  padding-top: 20px;
}

.form-control:focus {
  border-color: var(--primary) !important;
  box-shadow: 0 0 0 2px rgba(185, 183, 211, 0.712);
}

.dotted-spinner {
  width: 18px;
  height: 18px;
  border: 3px dotted #fff;
  border-radius: 50%;
  animation: spin 1.5s linear infinite;
}

@keyframes spin {
  100% {
    transform: rotate(360deg);
  }
}
</style>